// ─── PreviewPanel — file inline preview (본인 파일만) ────────────────────────

import { useTranslation } from 'react-i18next';
import type { AttachmentPreviewMeta } from '../types/attachment';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PreviewPanelProps {
  fileId: string | null;
  meta: AttachmentPreviewMeta | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  originalName?: string;
  onClose: () => void;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function PreviewSkeleton() {
  const { t } = useTranslation();
  return (
    <div
      aria-busy="true"
      aria-label={t('attachmentDemo.preview.loadingLabel')}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}
    >
      {[100, 70, 55].map((w, i) => (
        <div
          key={i}
          style={{
            height: i === 0 ? 180 : 14,
            width: `${w}%`,
            background:
              'linear-gradient(90deg, var(--color-surface-elevated), color-mix(in srgb, var(--color-surface-elevated) 50%, var(--color-surface-muted)), var(--color-surface-elevated))',
            borderRadius: 'var(--radius-sm)',
          }}
        />
      ))}
    </div>
  );
}

// ─── PreviewPanel ─────────────────────────────────────────────────────────────

export function PreviewPanel({
  fileId,
  meta,
  isLoading,
  isError,
  errorMessage,
  originalName,
  onClose,
}: PreviewPanelProps) {
  const { t } = useTranslation();
  return (
    <aside
      aria-label={t('attachmentDemo.preview.title')}
      style={{
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 360,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-elevated)',
          borderBottom: '1px solid var(--color-surface-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--spacing-2)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          <span aria-hidden="true">🖼</span>
          {originalName ? (
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 180,
              }}
              title={originalName}
            >
              {originalName}
            </span>
          ) : (
            t('attachmentDemo.preview.title')
          )}
        </span>
        <button
          type="button"
          aria-label={t('attachmentDemo.preview.closeAriaLabel')}
          onClick={onClose}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-base)',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 'var(--radius-sm)',
          }}
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div
        style={{
          padding: 'var(--spacing-4)',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-3)',
          alignItems: 'stretch',
        }}
      >
        {/* Idle — no file selected */}
        {!fileId && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'var(--color-text-muted)',
              fontStyle: 'italic',
              fontSize: 'var(--font-size-sm)',
              textAlign: 'center',
              padding: 'var(--spacing-12)',
            }}
          >
            {t('attachmentDemo.preview.idle')}
          </div>
        )}

        {/* Loading */}
        {fileId && isLoading && <PreviewSkeleton />}

        {/* Error */}
        {fileId && !isLoading && isError && (
          <div
            role="alert"
            style={{
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-4)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            <strong>{t('attachmentDemo.preview.failed')}</strong>
            {errorMessage && (
              <p style={{ marginTop: 'var(--spacing-1)', color: 'var(--color-text-primary)', margin: 0 }}>
                {errorMessage}
              </p>
            )}
          </div>
        )}

        {/* Preview content */}
        {fileId && !isLoading && !isError && meta && (
          <>
            {/* IMAGE */}
            {meta.kind === 'IMAGE' && meta.previewUrl && (
              <div
                style={{
                  background: 'var(--color-surface-background)',
                  border: '1px dashed var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  aspectRatio: '1 / 1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={meta.previewUrl}
                  alt={originalName ?? t('attachmentDemo.preview.title')}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  loading="lazy"
                />
              </div>
            )}

            {/* PDF */}
            {meta.kind === 'PDF' && meta.previewUrl && (
              <object
                data={meta.previewUrl}
                type="application/pdf"
                aria-label={`${originalName ?? 'PDF'} ${t('attachmentDemo.preview.title')}`}
                style={{
                  width: '100%',
                  flex: 1,
                  minHeight: 320,
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <p style={{ padding: 'var(--spacing-4)', color: 'var(--color-text-secondary)' }}>
                  {t('attachmentDemo.preview.pdfFallback')}{' '}
                  <a
                    href={meta.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-brand-primary)' }}
                  >
                    {t('attachmentDemo.preview.openNewTab')}
                  </a>
                </p>
              </object>
            )}

            {/* TEXT / CSV */}
            {meta.kind === 'TEXT' && meta.previewUrl && (
              <pre
                aria-label={t('attachmentDemo.preview.contentsAriaLabel', { name: originalName ?? t('attachmentDemo.preview.title') })}
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  background: 'var(--color-surface-background)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--spacing-3)',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight: 320,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  margin: 0,
                }}
              >
                {/* Text will be loaded as an iframe or fetched separately */}
                <iframe
                  src={meta.previewUrl}
                  title={originalName ?? t('attachmentDemo.preview.title')}
                  style={{ width: '100%', border: 'none', minHeight: 200 }}
                />
              </pre>
            )}

            {/* UNSUPPORTED */}
            {meta.kind === 'UNSUPPORTED' && (
              <div
                style={{
                  padding: 'var(--spacing-6)',
                  textAlign: 'center',
                  background: 'var(--color-surface-elevated)',
                  border: '1px dashed var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <span aria-hidden="true" style={{ display: 'block', fontSize: 40, marginBottom: 'var(--spacing-3)' }}>
                  🚫
                </span>
                {t('attachmentDemo.preview.unsupportedFormat', { mime: meta.mimeType })}
                <p
                  style={{
                    marginTop: 'var(--spacing-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {t('attachmentDemo.preview.unsupportedDesc')}
                </p>
              </div>
            )}

            {/* Meta row */}
            <dl
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                display: 'grid',
                gridTemplateColumns: '80px 1fr',
                gap: 'var(--spacing-1) var(--spacing-2)',
                margin: 0,
              }}
            >
              <dt style={{ color: 'var(--color-text-muted)' }}>MIME</dt>
              <dd style={{ margin: 0, color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>
                {meta.mimeType}
              </dd>
              <dt style={{ color: 'var(--color-text-muted)' }}>kind</dt>
              <dd style={{ margin: 0, color: 'var(--color-brand-primary)' }}>{meta.kind}</dd>
              {meta.presignTtl && (
                <>
                  <dt style={{ color: 'var(--color-text-muted)' }}>TTL</dt>
                  <dd style={{ margin: 0 }}>{meta.presignTtl}</dd>
                </>
              )}
            </dl>
          </>
        )}
      </div>
    </aside>
  );
}
